// Cluster map: categories → trends → topics, force-laid-out around a ring.
//
// Lifted from H3-BFSI Lens vendor/dashboard.js (renderClusterMap), with three
// changes:
//   1. Pure render function — takes data as argument, returns a destroy().
//   2. Reads the trend tracker schema (topics + topic_trends + trends +
//      categories) instead of the Lens "captures with trend tags" shape.
//   3. Deterministic seed positions BEFORE the sim — fixes the "loads
//      broken for minutes" regression we hit in the Lens (v0.27.1 fix).

import * as d3 from "d3";
import type { Category, Topic, TopicTrend, Trend } from "../lib/types.ts";

export interface ClusterData {
  categories: Category[];
  trends: Trend[];
  topics: Topic[];
  topicTrends: TopicTrend[];
}

export interface ClusterCallbacks {
  onTopicClick: (topicId: number) => void;
}

interface Node {
  id: string;
  type: "category" | "trend" | "topic";
  refId: number;
  label: string;
  r: number;
  color: string;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface Edge {
  source: string;
  target: string;
}

const PALETTE = [
  "#7b6cff", "#56e1d3", "#6ba8ff", "#ff7b9c", "#ffc56a", "#9bf0c4", "#ff9e7a",
];

export function renderClusterMap(
  host: HTMLElement,
  data: ClusterData,
  cb: ClusterCallbacks,
): () => void {
  host.innerHTML = "";
  const W = Math.max(host.clientWidth || 0, 720);
  const H = Math.max(host.clientHeight || 0, 520);

  // Build nodes + edges. Categories become hubs; trends orbit their primary
  // category; topics orbit their primary trend (with secondary memberships
  // shown as faint cross-edges).
  const colorByCat = new Map<number, string>();
  data.categories.forEach((c, i) => colorByCat.set(c.id, PALETTE[i % PALETTE.length]));

  const nodes: Node[] = [];
  for (const c of data.categories) {
    nodes.push({
      id: `c:${c.id}`, type: "category", refId: c.id, label: c.name,
      r: 22, color: colorByCat.get(c.id)!,
    });
  }
  for (const t of data.trends) {
    nodes.push({
      id: `tr:${t.id}`, type: "trend", refId: t.id, label: t.name,
      r: 14, color: colorByCat.get(t.category_id) ?? "#888",
    });
  }
  for (const tp of data.topics) {
    // Pick the topic's primary trend's category for color
    const primary = data.topicTrends.find(
      (tt) => tt.topic_id === tp.id && tt.is_primary,
    );
    const primaryTrend = primary
      ? data.trends.find((t) => t.id === primary.trend_id)
      : undefined;
    const color = primaryTrend
      ? colorByCat.get(primaryTrend.category_id) ?? "#888"
      : "#888";
    nodes.push({
      id: `tp:${tp.id}`, type: "topic", refId: tp.id, label: tp.name, r: 8, color,
    });
  }

  const edges: Edge[] = [];
  for (const t of data.trends) {
    edges.push({ source: `c:${t.category_id}`, target: `tr:${t.id}` });
  }
  for (const tt of data.topicTrends) {
    edges.push({ source: `tr:${tt.trend_id}`, target: `tp:${tt.topic_id}` });
  }

  // Deterministic seed positions: categories on a tight inner ring, trends on
  // a middle ring, topics on an outer ring. The force sim then polishes; it
  // doesn't have to do all the placement work.
  const cx = W / 2, cy = H / 2;
  const seedRing = (subset: Node[], radius: number) => {
    const n = Math.max(1, subset.length);
    subset.forEach((node, i) => {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      node.x = cx + Math.cos(angle) * radius;
      node.y = cy + Math.sin(angle) * radius;
    });
  };
  seedRing(nodes.filter((n) => n.type === "category"), Math.min(W, H) * 0.12);
  seedRing(nodes.filter((n) => n.type === "trend"), Math.min(W, H) * 0.27);
  seedRing(nodes.filter((n) => n.type === "topic"), Math.min(W, H) * 0.42);

  // SVG + zoom
  const svg = d3.select(host).append("svg")
    .attr("class", "cluster-svg")
    .attr("viewBox", `0 0 ${W} ${H}`);
  const g = svg.append("g");
  const zoom = d3.zoom<SVGSVGElement, unknown>()
    .scaleExtent([0.35, 4])
    .on("zoom", (e) => g.attr("transform", e.transform.toString()));
  svg.call(zoom);

  // Tooltip
  const tip = document.createElement("div");
  tip.className = "viz-tip";
  tip.style.display = "none";
  host.appendChild(tip);

  // Edges
  const edgeSel = g.append("g").attr("class", "edges")
    .selectAll<SVGLineElement, Edge>("line").data(edges).join("line")
    .attr("class", "cluster-edge");

  // Nodes
  const nodeSel = g.append("g").attr("class", "nodes")
    .selectAll<SVGGElement, Node>("g.node").data(nodes, (d) => d.id).join("g")
    .attr("class", (d) => `node node-${d.type}`)
    .style("cursor", (d) => d.type === "topic" ? "pointer" : "default");

  nodeSel.append("circle")
    .attr("r", (d) => d.r)
    .attr("fill", (d) => d.color)
    .attr("opacity", (d) => d.type === "category" ? 0.9 : 0.75);

  nodeSel.append("text")
    .attr("dy", (d) => d.type === "topic" ? d.r + 12 : 4)
    .attr("text-anchor", "middle")
    .attr("class", (d) => `node-label node-label-${d.type}`)
    .attr("opacity", (d) => d.type === "topic" ? 0 : 1)
    .text((d) => d.label.length > 28 ? d.label.slice(0, 27) + "…" : d.label);

  nodeSel.on("mouseenter.label", function () {
    d3.select(this).select("text").attr("opacity", 1);
  }).on("mouseleave.label", function () {
    const isTopic = d3.select(this).classed("node-topic");
    if (isTopic) d3.select(this).select("text").attr("opacity", 0);
  });

  nodeSel.on("mouseenter", (_e, d) => {
    tip.innerHTML = `<strong>${d.label}</strong><br><span class="muted">${d.type}</span>`;
    tip.style.display = "block";
  })
  .on("mousemove", (e) => {
    const r = host.getBoundingClientRect();
    tip.style.left = `${e.clientX - r.left + 14}px`;
    tip.style.top = `${e.clientY - r.top + 14}px`;
  })
  .on("mouseleave", () => { tip.style.display = "none"; })
  .on("click", (e, d) => {
    e.stopPropagation();
    if (d.type === "topic") cb.onTopicClick(d.refId);
  });

  // Paint at seed positions immediately so first frame is legible.
  nodeSel.attr("transform", (d) => `translate(${d.x},${d.y})`);
  edgeSel
    .attr("x1", (d) => nodes.find((n) => n.id === d.source)?.x ?? 0)
    .attr("y1", (d) => nodes.find((n) => n.id === d.source)?.y ?? 0)
    .attr("x2", (d) => nodes.find((n) => n.id === d.target)?.x ?? 0)
    .attr("y2", (d) => nodes.find((n) => n.id === d.target)?.y ?? 0);

  // Force sim — polishing only. Short alpha, fast decay, hard stop.
  const link = d3.forceLink<Node, Edge>(edges as any).id((d) => d.id)
    .distance((e) => {
      const a = nodes.find((n) => n.id === (e as any).source.id || n.id === (e as any).source);
      return a?.type === "category" ? 80 : 40;
    })
    .strength(0.3);
  const sim = d3.forceSimulation<Node>(nodes)
    .force("link", link)
    .force("charge", d3.forceManyBody<Node>().strength((d) =>
      d.type === "category" ? -400 : d.type === "trend" ? -180 : -50,
    ))
    .force("collide", d3.forceCollide<Node>().radius((d) => d.r + 4).strength(0.9))
    .alpha(0.35)
    .alphaDecay(0.12)
    .alphaMin(0.03)
    .on("tick", () => {
      edgeSel
        .attr("x1", (e) => ((e as any).source.x))
        .attr("y1", (e) => ((e as any).source.y))
        .attr("x2", (e) => ((e as any).target.x))
        .attr("y2", (e) => ((e as any).target.y));
      nodeSel.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });
  const stopTimer = setTimeout(() => sim.stop(), 1500);

  // Drag — clickDistance(5) so short clicks still fire the topic-click handler.
  const drag = d3.drag<SVGGElement, Node>()
    .clickDistance(5)
    .on("start", (event, d) => {
      if (!event.active) sim.alphaTarget(0.3).restart();
      d.fx = d.x; d.fy = d.y;
    })
    .on("drag", (event, d) => { d.fx = event.x; d.fy = event.y; })
    .on("end", (event, d) => {
      if (!event.active) sim.alphaTarget(0);
      d.fx = null; d.fy = null;
    });
  nodeSel.call(drag);

  return () => {
    clearTimeout(stopTimer);
    sim.stop();
    host.innerHTML = "";
  };
}
